import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useNavigate } from "../lib/nav";
import { Button, Icon, Input, Logo } from "@omnabase/ui";
import type { Me } from "@omnabase/shared";
import { authClient } from "../lib/auth";
import { signOutHere } from "../lib/push";
import { isCode, isEmail, landingAfterProof, landingPath, nextPath, normaliseCode, problemFor, stepFrom } from "../lib/signin";
import { signedQueryOf } from "../lib/oauthConsent";
import { loadTurnstile, turnstileToken } from "../lib/turnstile";
import { autoAccept } from "../lib/invitations";
import { fetchMe, wrote } from "../lib/api";
import { InvitationList, NewWorkspaceStep, WelcomeStep, joinedToast } from "./signin/SignInSteps";

/** Signing in: who you are, then where to. Ported from the prototype's SignInScreen; the same steps in the URL, the same split, now
 *  against the API: the code arrives by mail, the workspace list is the person's memberships. M11 round one: after the code the person
 *  lands by the brief's order (`landingAfterProof`), a newcomer's invitations and a followed link's are accepted without a click, and a
 *  person in no workspace reaches the welcome step and the creation form instead of the email form again. */
export function SignInScreen({ me, onSignedIn }: { me: Me | null; onSignedIn: () => Promise<void> }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const asked = stepFrom(params.get("step"));
  const step = (asked === "welcome" || asked === "new") && !me ? "email" : asked; // the two new steps are for a signed-in person
  const email = params.get("email") ?? "";
  const named = params.get("workspace");
  const oauthQuery = signedQueryOf(params.toString()); // M9 round two: an app's signed sign-in request, carried through the steps in the address
  const [typed, setTyped] = useState(email);
  const [proof, setProof] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const turnstileEl = useRef<HTMLDivElement>(null);
  const codeTurnstileEl = useRef<HTMLDivElement>(null); // M11 round one: the code step's own widget, so "Send it again" carries a fresh token
  /** one update for several keys: two updates in a row would each start from the stale params and undo each other (CLAUDE.md, state in the URL) */
  const patchParams = (patch: Record<string, string | null>) => { const p = new URLSearchParams(params); for (const [k, v] of Object.entries(patch)) { if (v === null) p.delete(k); else p.set(k, v); } setParams(p); };

  useEffect(() => { void loadTurnstile(); }, []);
  useEffect(() => { if (step === "workspace" && me && me.workspaces.length <= 1) navigate(me.workspaces[0] ? `/${me.workspaces[0].slug}` : "/signin?step=welcome", { replace: true }); }, [step, me, navigate]);

  const enter = (slug: string) => navigate(`/${slug}`);
  /** an invitation accepted from a list (the welcome step, the chooser): the workspace opens with "You joined …" */
  const joinedFromList = async (j: { id: string; slug: string; name: string }) => { await onSignedIn(); joinedToast([j]); enter(j.slug); };

  const submitEmail = async (e: FormEvent) => {
    e.preventDefault();
    if (!isEmail(typed)) { setProblem("Enter your email address."); return; }
    setProblem(null); setBusy(true);
    const token = turnstileEl.current ? await turnstileToken(turnstileEl.current) : undefined;
    const r = await authClient.emailOtp.sendVerificationOtp({ email: typed.trim(), type: "sign-in", fetchOptions: { headers: token ? { "x-turnstile-token": token } : {} } });
    setBusy(false);
    if (r.error) { setProblem(problemFor(r.error.status, r.error.code)); return; }
    patchParams({ email: typed.trim(), step: "code" });
  };

  const submitProof = async (e: FormEvent) => {
    e.preventDefault();
    if (!isCode(proof)) { setProblem("Enter the six digits from the mail."); return; }
    setProblem(null); setBusy(true);
    if (oauthQuery) { await signInForApp(oauthQuery); return; }
    const r = await authClient.signIn.emailOtp({ email, otp: normaliseCode(proof) });
    setBusy(false);
    if (r.error) { setProblem(problemFor(r.error.status, r.error.code)); return; }
    await landAfterProof();
  };

  /** M11 round one (choices 4 and 5): auto-accept where the proof is strong, then the landing order; a followed link that cannot be used
   *  shows its reason on the invitation's page */
  const landAfterProof = async () => {
    const invite = params.get("invite");
    const before = await fetchMe();
    const { joined, followedFailed } = await autoAccept(invite, before?.workspaces.length ?? 0);
    await onSignedIn();
    if (followedFailed && invite) { navigate(`/invite/${encodeURIComponent(invite)}`, { replace: true }); return; }
    const fresh = joined.length ? await fetchMe() : before;
    const landing = landingAfterProof({ workspaces: fresh?.workspaces ?? [], lastWorkspace: fresh?.lastWorkspace ?? null, named, invitation: joined[0]?.slug ?? null });
    joinedToast(joined);
    const back = joined.length ? null : nextPath(params.get("next"), fresh?.workspaces ?? []); // the forum sync round: back to the place the signed-out sheet or a workspace address named
    if (back) navigate(back, { replace: true });
    else if ("slug" in landing) enter(landing.slug); else patchParams({ step: landing.step, next: null });
  };

  /** M9 round two (round zero 1d): Claude sent a signed-out member here with its signed sign-in request; the code sign-in carries it as
   *  `oauth_query`, and the library answers the consent page's address (never the inbox) */
  const signInForApp = async (oauth_query: string) => {
    wrote("/api/auth/sign-in");
    const r = await fetch("/api/auth/sign-in/email-otp", { method: "POST", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify({ email, otp: normaliseCode(proof), oauth_query }) });
    const body = await r.json().catch(() => null) as { url?: string; code?: string; error_description?: string } | null;
    if (r.ok && body?.url) { window.location.assign(body.url); return; }
    setBusy(false);
    setProblem(r.status === 403 && body?.error_description ? body.error_description.replace(/^\w+: /, "") : problemFor(r.status, body?.code));
  };

  /** "Send it again": a fresh token from the code step's own widget (a token works once; the API refuses a send without one) */
  const resend = async () => {
    const token = codeTurnstileEl.current ? await turnstileToken(codeTurnstileEl.current) : undefined;
    const r = await authClient.emailOtp.sendVerificationOtp({ email, type: "sign-in", fetchOptions: { headers: token ? { "x-turnstile-token": token } : {} } });
    setProblem(r.error ? problemFor(r.error.status, r.error.code) : `A new code is on its way to ${email}`);
  };
  const notYou = () => { setProof(""); patchParams({ step: null, email: null }); };

  /** "Not you?" on the welcome step: the session is someone else's address, so it ends before the email form */
  const signOutAndRestart = async () => { await signOutHere(); await onSignedIn(); notYou(); };

  return (
    <div className="ob-signin">
      <aside className="ob-signin__aside" aria-hidden="true">
        <div className="ob-signin__aside-inner">
          {named ? <span className="ob-signin__mark">{named.slice(0, 2).toUpperCase()}</span> : <span className="ob-signin__logo"><Logo /></span>}
          <h2 className="ob-signin__claim">{named ? `Sign in to ${named}` : oauthQuery ? "Sign in to connect your AI" : "Welcome to OmnaBase."}</h2>
          <p className="ob-signin__line">{named ? "Your team's support inbox, with an AI that answers first." : "Your support inbox, with an AI that answers first."}</p>
        </div>
      </aside>
      <main className="ob-signin__main" aria-label="Sign in">
        <div className="ob-signin__box">
          {step === "email" && (
            <form className="ob-signin__form" onSubmit={submitEmail} noValidate>
              <h1 className="ob-signin__title">Sign in</h1>
              <label className="ob-signin__field"><span className="ob-signin__label">Email</span><Input className="ob-input--text" type="email" autoComplete="email" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="you@company.com" aria-label="Email" autoFocus /></label>
              <div ref={turnstileEl} className="ob-signin__turnstile" />
              {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
              <Button variant="primary" type="submit" className="ob-signin__btn" disabled={busy}>Continue</Button>
              <p className="ob-signin__foot">New to OmnaBase? Enter your email, type the code we send you, and set up your first workspace.</p>
              <p className="ob-signin__foot"><a className="ob-link" href="/legal/terms">Terms</a>{" · "}<a className="ob-link" href="/legal/privacy">Privacy</a>{" · "}<a className="ob-link" href="/legal/imprint">Imprint</a></p>
            </form>
          )}
          {step === "code" && (
            <form className="ob-signin__form" onSubmit={submitProof} noValidate>
              <h1 className="ob-signin__title">Check your mail</h1>
              <p className="ob-signin__intro">We sent a six-digit code to <strong>{email}</strong>. It is good for ten minutes.</p>
              <label className="ob-signin__field"><span className="ob-signin__label">Code</span><Input className="ob-input--text ob-signin__code" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={proof} onChange={(e) => setProof(e.target.value)} placeholder="123 456" aria-label="Code" autoFocus /></label>
              <div ref={codeTurnstileEl} className="ob-signin__turnstile" />
              {problem && <p className="ob-signin__problem" role="alert">{problem}</p>}
              <Button variant="primary" type="submit" className="ob-signin__btn" disabled={busy}>Sign in</Button>
              <p className="ob-signin__foot"><button type="button" className="ob-link" onClick={() => void resend()}>Send it again</button>{" · "}<button type="button" className="ob-link" onClick={notYou}>Not you?</button></p>
            </form>
          )}
          {step === "workspace" && me && (
            <div className="ob-signin__form">
              <h1 className="ob-signin__title">Where to?</h1>
              <p className="ob-signin__intro">{me.user.email} belongs to {me.workspaces.length} workspaces.</p>
              <div className="ob-signin__workspaces" role="list">
                {me.workspaces.map((w) => (
                  <button key={w.id} type="button" role="listitem" className="ob-signin__workspace" onClick={() => enter(w.slug)}>
                    <span className="ob-mark">{w.name.split(/\s+/).map((p) => p[0]).join("").slice(0, 2).toUpperCase()}</span>
                    <span className="ob-signin__ws-text"><span className="ob-signin__ws-name">{w.name}</span><span className="ob-signin__ws-meta">{w.role}</span></span>
                    <Icon name="chevronRight" size="sm" />
                  </button>
                ))}
              </div>
              <InvitationList onJoined={(j) => void joinedFromList(j)} />
              <p className="ob-signin__foot"><button type="button" className="ob-link" onClick={() => patchParams({ step: "new" })}>Start a new workspace</button></p>
            </div>
          )}
          {step === "welcome" && me && <WelcomeStep email={me.user.email} onCreate={() => patchParams({ step: "new" })} onJoined={(j) => void joinedFromList(j)} onNotYou={() => void signOutAndRestart()} />}
          {step === "new" && me && <NewWorkspaceStep onCreated={(slug) => void onSignedIn().then(() => enter(slug))} onCancel={() => (me.workspaces.length ? navigate(landingPath(landingAfterProof({ workspaces: me.workspaces, lastWorkspace: me.lastWorkspace, named: null, invitation: null }))) : patchParams({ step: "welcome" }))} />}
        </div>
        <p className="ob-signin__legal">OmnaBase</p>
      </main>
    </div>
  );
}
